import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { cn } from "../lib/utils";

interface Props {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned action buttons. */
  actions?: ReactNode;
  /** Inline element rendered right after the title (a status pill, a count…). */
  badge?: ReactNode;
  /** Optional "← back" link rendered above the title. `state` is passed to the
   *  Link (e.g. the row id for the list to highlight — see shared/listNavigation). */
  backTo?: { to: string; label: string; state?: unknown };
  /** Rendered full-width under the title row (a filter strip, a tab row…). */
  children?: ReactNode;
  className?: string;
}

/**
 * Standard page heading: bold title (+ optional pill), muted one-line
 * description, actions on the right, optional back link and tab strip.
 */
export default function PageHeader({ title, description, actions, badge, backTo, children, className }: Props) {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {backTo && (
        <Link
          to={backTo.to}
          state={backTo.state}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground w-fit transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {backTo.label}
        </Link>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-semibold text-foreground tracking-tight">{title}</h1>
            {badge}
          </div>
          {/* div, not p: callers pass inline rows / lists here, which can't nest inside a <p> */}
          {description && <div className="text-sm text-muted-foreground mt-0.5">{description}</div>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap shrink-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
